/**
 * FileLocation.authType lives on DynamoDB (JSON: authType). Credential maps in secretValue go to Secrets Manager — omit AuthType there.
 */
import {
  SFTP_AUTH_TYPE,
  effectiveSftpAuthType,
  isSftpSshKeyAuthType,
  type SftpAuthType,
} from "../constants/sftp-auth-type";
import { isJhaNetworkStorageType } from "../constants/jha-network-storage-type";

export function isSftpOrFtpsStorage(storageType: string | undefined | null): boolean {
  const s = String(storageType ?? "").toLowerCase();
  return s === "sftp" || s === "ftps";
}

/** SFTP, FTPS, and JHA Network hop storage on a workflow step endpoint. */
export function isHopCredentialStorageType(storageType: string | undefined | null): boolean {
  return isSftpOrFtpsStorage(storageType) || isJhaNetworkStorageType(storageType);
}

/**
 * Azure, S3 remote, and JHA Network keep hop secrets on the same FileInfo as StorageType
 * (destination for copy/move). Classic SFTP/FTPS still park secrets on source for the API normalizer.
 */
export function secretsPersistOnDestinationFileInfo(storageType: string | undefined | null): boolean {
  const s = String(storageType ?? "").toLowerCase();
  return s === "azure" || s === "amazons3remote" || isJhaNetworkStorageType(s);
}

/** Write hop secrets on one file info and clear the other so the same step is not sent twice. */
export function assignStepSecrets(
  sourceFileInfo: Record<string, unknown> | undefined,
  destinationFileInfo: Record<string, unknown> | undefined,
  step: { secretName?: string; secretValue?: Record<string, unknown> | null },
  secretsOnDestination: boolean,
) {
  const target = secretsOnDestination ? destinationFileInfo : sourceFileInfo;
  const other = secretsOnDestination ? sourceFileInfo : destinationFileInfo;
  if (target) {
    target.SecretName = step.secretName ?? "";
    const sv = step.secretValue;
    if (sv && sv.hideValue == null) {
      // Blank keys are the edit ***** mask. SFTP leaves those off the payload so the stored secret stays.
      target.SecretValue = omitBlankSecretValues(cloneSecretValueOmitAuthType(sv));
    }
  }
  if (other) {
    other.SecretName = "";
    other.SecretValue = {};
    delete other.authType;
    delete other.AuthType;
  }
}

type ApiFileInfoSlice = Record<string, unknown> | null | undefined;

function readSecretName(fi: ApiFileInfoSlice): string {
  return String(fi?.secretName ?? fi?.SecretName ?? "");
}

function readSecretValue(fi: ApiFileInfoSlice): Record<string, unknown> | null | undefined {
  const sv = fi?.secretValue ?? fi?.SecretValue;
  if (!sv || typeof sv !== "object" || Array.isArray(sv)) return undefined;
  return sv as Record<string, unknown>;
}

function secretValueHasKeys(sv: Record<string, unknown> | null | undefined): boolean {
  return !!sv && typeof sv === "object" && Object.keys(sv).length > 0;
}

/**
 * Map API file infos into wizard step credentials. Pull/Push filecopy+filemove keep hop secrets on
 * destination after persist; legacy payloads may still park them on source.
 */
export function resolveWizardStepHopCredentialsFromApiFileInfos(
  sourceFileInfo: ApiFileInfoSlice,
  destinationFileInfo: ApiFileInfoSlice,
  endpointStorageType: string | undefined | null,
): { secretName: string; secretValue: Record<string, unknown> | null | undefined } {
  const st = String(endpointStorageType ?? "").toLowerCase();
  if (st === "azure" || st === "amazons3remote") {
    return {
      secretName: readSecretName(destinationFileInfo),
      secretValue: readSecretValue(destinationFileInfo),
    };
  }
  if (isHopCredentialStorageType(st)) {
    const destSv = readSecretValue(destinationFileInfo);
    const srcSv = readSecretValue(sourceFileInfo);
    return {
      secretName: readSecretName(destinationFileInfo) || readSecretName(sourceFileInfo),
      secretValue: secretValueHasKeys(destSv) ? destSv : srcSv,
    };
  }
  return {
    secretName: readSecretName(sourceFileInfo),
    secretValue: readSecretValue(sourceFileInfo),
  };
}

export type WizardStepSftpCredentialsSlice = {
  storageType?: string;
  StorageType?: string;
  AuthType?: string;
  authType?: string;
  secretValue?: Record<string, unknown> | null;
};

/**
 * Clone / wizard list validation: Host, Port, UserName required; then Password (FTPS or SFTP password)
 * or private key / Secrets reference (SFTP SSH key). Matches AddWorkflowStepPopup.addSecretValue rules.
 */
export function wizardStepSftpFtpsSecretsComplete(step: WizardStepSftpCredentialsSlice): boolean {
  const st = String(step.storageType ?? step.StorageType ?? "").toLowerCase();
  if (st !== "sftp" && st !== "ftps") {
    return false;
  }
  const sv = step.secretValue;
  if (!sv || typeof sv !== "object") {
    return false;
  }
  const host = String(sv.Host ?? "").trim();
  const port = String(sv.Port ?? "").trim();
  const user = String(sv.UserName ?? "").trim();
  if (!host || !port || !user) {
    return false;
  }
  if (st === "ftps") {
    return !!String(sv.Password ?? "").trim();
  }
  const authRaw = step.AuthType ?? step.authType;
  if (isSftpSshKeyAuthType(authRaw)) {
    const pk = String(sv.privateKey ?? sv.PrivateKey ?? "").trim();
    const ref = String(sv.SshPrivateKeyReference ?? sv.sshPrivateKeyReference ?? "").trim();
    return !!(pk || ref);
  }
  return !!String(sv.Password ?? "").trim();
}

export type SftpAuthLeg = "source" | "destination";

/** Step may carry optional per-leg auth (camelCase or PascalCase) in addition to AuthType for the edited endpoint. */
export type SftpAuthStepSlice = {
  storageType?: string;
  StorageType?: string;
  AuthType?: string;
  authType?: string;
  sourceAuthType?: string;
  SourceAuthType?: string;
  destinationAuthType?: string;
  DestinationAuthType?: string;
  secretValue?: Record<string, unknown> | null;
};

function legAuthRaw(step: SftpAuthStepSlice, leg: SftpAuthLeg): string | undefined {
  const fallback = step.AuthType ?? step.authType;
  if (leg === "source") {
    return (step.sourceAuthType ?? step.SourceAuthType ?? fallback) as string | undefined;
  }
  return (step.destinationAuthType ?? step.DestinationAuthType ?? fallback) as string | undefined;
}

/** Effective Password/SshKey for a given leg (independent Dynamo authType per SFTP/FTPS file location). */
export function sftpAuthTypeForLeg(step: SftpAuthStepSlice, leg: SftpAuthLeg): SftpAuthType {
  return effectiveSftpAuthType(legAuthRaw(step, leg));
}

export function applyFileLocationAuthTypeIfSftpFtps(
  fileInfo: Record<string, unknown> | undefined,
  step: SftpAuthStepSlice,
  leg?: SftpAuthLeg
) {
  if (!fileInfo) return;
  const st = String(fileInfo.StorageType ?? fileInfo.storageType ?? "").toLowerCase();
  if (!isHopCredentialStorageType(st)) return;
  applyFileLocationAuthType(fileInfo, step, leg);
}

/**
 * PUSH `filemove` puts SFTP/FTPS `SecretName` on `SourceFileInfo` while `StorageType` lives on `DestinationFileInfo`.
 * Copy the same Dynamo auth mode onto `SourceFileInfo` using the endpoint leg (`destination`) so both blocks include `authType`.
 */
export function applyPushSftpAuthTypeToCredentialSourceFileInfo(
  sourceFileInfo: Record<string, unknown> | undefined,
  step: SftpAuthStepSlice,
  stepStorageType: string | undefined | null
) {
  if (!sourceFileInfo) return;
  const srcSt = String(sourceFileInfo.StorageType ?? sourceFileInfo.storageType ?? "").toLowerCase();
  if (isHopCredentialStorageType(srcSt)) return;
  if (!isHopCredentialStorageType(stepStorageType)) return;
  applyFileLocationAuthType(sourceFileInfo, step, "destination");
}

export function cloneSecretValueOmitAuthType(
  sv: Record<string, unknown> | undefined | null
): Record<string, string> {
  if (!sv || typeof sv !== "object" || Array.isArray(sv)) return {};
  const { AuthType: _a, authType: _b, ...rest } = sv as Record<string, unknown>;
  return { ...(rest as Record<string, string>) };
}

function omitBlankSecretValues(secretValue: Record<string, string>): Record<string, string> {
  const kept: Record<string, string> = {};
  for (const [key, value] of Object.entries(secretValue)) {
    if (String(value ?? "").trim() !== "") {
      kept[key] = value;
    }
  }
  return kept;
}

/** Values for FileLocation authType (DynamoDB / Workflow Service). */
export function fileLocationAuthTypeForApiStep(
  step: SftpAuthStepSlice,
  leg?: SftpAuthLeg
): string | undefined {
  if (!leg) {
    const st = String(step?.storageType ?? step?.StorageType ?? "").toLowerCase();
    if (st !== "sftp" && st !== "ftps") return undefined;
    return effectiveSftpAuthType(step.AuthType ?? step.authType) === SFTP_AUTH_TYPE.SshKey ? "ssh" : "password";
  }
  return sftpAuthTypeForLeg(step, leg) === SFTP_AUTH_TYPE.SshKey ? "ssh" : "password";
}

export function applyFileLocationAuthType(
  fileInfo: Record<string, unknown> | undefined,
  step: SftpAuthStepSlice,
  leg?: SftpAuthLeg
) {
  if (!fileInfo) return;
  const at = leg ? fileLocationAuthTypeForApiStep(step, leg) : fileLocationAuthTypeForApiStep(step);
  if (at !== undefined) {
    fileInfo.authType = at;
    delete fileInfo.AuthType;
  }
}

/** After loading secretValue from API file info, set wizard AuthType from Dynamo authType or legacy secret. */
export function syncStepAuthTypeFromFileInfo(
  step: { AuthType?: string; secretValue?: Record<string, unknown> | null },
  fileInfo: { authType?: string; AuthType?: string; secretValue?: Record<string, unknown> | null } | null | undefined
) {
  if (!fileInfo) return;
  const fromFi = fileInfo.authType ?? fileInfo.AuthType;
  const fromNested = fileInfo.secretValue?.AuthType ?? fileInfo.secretValue?.authType;
  const fromStepSv = step.secretValue?.AuthType ?? step.secretValue?.authType;
  step.AuthType = effectiveSftpAuthType((fromFi ?? fromNested ?? fromStepSv) as string);
}

/** Sync per-leg auth from API FileLocation objects; sets sourceAuthType, destinationAuthType, and AuthType for the edited leg. */
export function syncStepAuthTypesFromBothFileInfos(
  step: Record<string, unknown>,
  sourceFileInfo: Record<string, unknown> | null | undefined,
  destinationFileInfo: Record<string, unknown> | null | undefined
) {
  const readFi = (fi: Record<string, unknown> | null | undefined) => {
    if (!fi) return SFTP_AUTH_TYPE.Password;
    const fromFi = fi.authType ?? fi.AuthType;
    const sv = (fi.secretValue ?? fi.SecretValue ?? {}) as Record<string, unknown>;
    const fromNested = sv.AuthType ?? sv.authType;
    return effectiveSftpAuthType((fromFi ?? fromNested) as string);
  };
  const src = readFi(sourceFileInfo);
  const dst = readFi(destinationFileInfo);
  step.sourceAuthType = src;
  step.destinationAuthType = dst;
  const st = String(step.storageType ?? step.StorageType ?? "").toLowerCase();
  const srcSt = String(sourceFileInfo?.storageType ?? sourceFileInfo?.StorageType ?? "").toLowerCase();
  const dstSt = String(destinationFileInfo?.storageType ?? destinationFileInfo?.StorageType ?? "").toLowerCase();
  const stepSn = String(step.secretName ?? step.SecretName ?? "");
  const srcSn = String(sourceFileInfo?.secretName ?? sourceFileInfo?.SecretName ?? "");
  const dstSn = String(destinationFileInfo?.secretName ?? destinationFileInfo?.SecretName ?? "");
  if (isSftpOrFtpsStorage(st)) {
    if (isSftpOrFtpsStorage(srcSt) && isSftpOrFtpsStorage(dstSt) && st === srcSt && st === dstSt) {
      if (stepSn && dstSn && stepSn === dstSn && stepSn !== srcSn) {
        step.AuthType = dst;
      } else if (stepSn && srcSn && stepSn === srcSn) {
        step.AuthType = src;
      } else {
        step.AuthType = src;
      }
    } else if (st === srcSt) {
      step.AuthType = src;
    } else if (st === dstSt) {
      step.AuthType = dst;
    } else if (isSftpOrFtpsStorage(srcSt)) {
      step.AuthType = src;
    } else if (isSftpOrFtpsStorage(dstSt)) {
      step.AuthType = dst;
    } else {
      step.AuthType = effectiveSftpAuthType(step.AuthType as string);
    }
  } else {
    step.AuthType = effectiveSftpAuthType((step.AuthType as string) ?? src ?? dst);
  }
}
